import { useCookieConsent } from "@/hooks/use-cookie-consent";
import { listenForMetaContactConversion } from "@/lib/meta-contact-conversion";
import { initializeMetaPixel } from "@/lib/meta-pixel-runtime";
import { listenForGoogleContactConversion } from "@/lib/google-contact-conversion";
import { api } from "@/convex/_generated/api";
import { useQuery } from "convex/react";
import { useEffect } from "react";
import { useLocation } from "react-router";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: {
      (...args: unknown[]): void;
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[][];
      loaded?: boolean;
      version?: string;
      push?: (...args: unknown[]) => void;
    };
    _fbq?: Window["fbq"];
    __enkeyGoogleTagState?: {
      measurementId: string;
      configured: boolean;
      lastPage: string;
    };
  }
}

function addScript(id: string, src: string) {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.src === src) return;
  existing?.remove();
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function removeScript(id: string) {
  document.getElementById(id)?.remove();
}

function syncResourceHints(idPrefix: string, enabled: boolean, origin: string) {
  const definitions = [
    { suffix: "preconnect", rel: "preconnect", href: origin },
    {
      suffix: "dns-prefetch",
      rel: "dns-prefetch",
      href: `//${new URL(origin).host}`,
    },
  ];
  for (const definition of definitions) {
    const id = `${idPrefix}-${definition.suffix}`;
    if (!enabled) {
      document.getElementById(id)?.remove();
      continue;
    }
    if (document.getElementById(id)) continue;
    const link = document.createElement("link");
    link.id = id;
    link.rel = definition.rel;
    link.href = definition.href;
    link.dataset.consentManaged = "true";
    document.head.appendChild(link);
  }
}

function clearCookie(name: string) {
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/; SameSite=Lax`;
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/; domain=.${location.hostname}; SameSite=Lax`;
}

const VALID_PUBLIC_IDS: Partial<Record<string, RegExp>> = {
  google_analytics: /^G-[A-Z0-9]{5,}$/i,
  google_tag_manager: /^GTM-[A-Z0-9]{5,}$/i,
  meta_pixel: /^\d{5,}$/,
};

function validPublicId(provider: string, value: string | undefined) {
  if (!value) return false;
  const pattern = VALID_PUBLIC_IDS[provider];
  return pattern ? pattern.test(value.trim()) : true;
}

function ensureGoogleTagQueue() {
  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function (...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  return window.gtag;
}

function googleConsentState(consent: {
  analytics: boolean;
  marketing: boolean;
}) {
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  return {
    ad_storage: marketing,
    analytics_storage: analytics,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
}

export default function PublicIntegrations() {
  const { consent } = useCookieConsent();
  const integrations = useQuery(
    api.cms.publicIntegrations,
    consent?.analytics === true || consent?.marketing === true ? {} : "skip",
  );
  const location = useLocation();

  useEffect(() => {
    const analytics = integrations?.find((item) => item.provider === "google_analytics" && item.consentCategory === "analytics");
    return listenForGoogleContactConversion({
      analyticsConsent: consent?.analytics === true,
      measurementId: analytics?.publicIdentifier,
      privateArea: /^\/(crm|login|register)(\/|$)/.test(location.pathname),
    });
  }, [consent?.analytics, integrations, location.pathname]);

  useEffect(() => {
    const meta = integrations?.find((item) => item.provider === "meta_pixel" && item.consentCategory === "marketing");
    return listenForMetaContactConversion({
      marketingConsent: consent?.marketing === true,
      pixelId: meta?.publicIdentifier,
      privateArea: /^\/(crm|login|register)(\/|$)/.test(location.pathname),
    });
  }, [consent?.marketing, integrations, location.pathname]);

  useEffect(() => {
    if (!integrations) return;
    const isPrivateArea =
      location.pathname.startsWith("/crm") ||
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/register");
    const allowed = (category: string) =>
      !isPrivateArea &&
      (category === "necessary" ||
        (category === "analytics" && consent?.analytics === true) ||
        (category === "marketing" && consent?.marketing === true));

    const analytics = integrations.find(
      (integration) =>
        integration.provider === "google_analytics" &&
        integration.consentCategory === "analytics" &&
        validPublicId(integration.provider, integration.publicIdentifier),
    );
    const analyticsAllowed =
      Boolean(analytics) && !isPrivateArea && consent?.analytics === true;
    if (analytics && analyticsAllowed) {
      const id = analytics.publicIdentifier.trim();
      const gtag = ensureGoogleTagQueue();
      gtag(
        "consent",
        "update",
        googleConsentState({
          analytics: consent?.analytics === true,
          marketing: consent?.marketing === true,
        }),
      );
      addScript(
        "enkey-google-analytics",
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`,
      );
      if (
        !window.__enkeyGoogleTagState?.configured ||
        window.__enkeyGoogleTagState.measurementId !== id
      ) {
        gtag("js", new Date());
        gtag("config", id, {
          anonymize_ip: true,
          send_page_view: false,
        });
        window.__enkeyGoogleTagState = {
          measurementId: id,
          configured: true,
          lastPage: "",
        };
      }
      const pagePath = `${location.pathname}${location.search}`;
      if (window.__enkeyGoogleTagState.lastPage !== pagePath) {
        gtag("event", "page_view", {
          page_path: pagePath,
          page_location: window.location.href,
          page_title: document.title,
        });
        window.__enkeyGoogleTagState.lastPage = pagePath;
      }
    } else {
      ensureGoogleTagQueue()(
        "consent",
        "update",
        googleConsentState({ analytics: false, marketing: false }),
      );
      removeScript("enkey-google-analytics");
      removeScript("enkey-google-analytics-config");
      removeScript("enkey-google-consent-default");
      window.__enkeyGoogleTagState = undefined;
    }
    if (!analyticsAllowed) {
      for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim();
        if (name && (name.startsWith("_ga") || name === "_gid")) {
          clearCookie(name);
        }
      }
    }

    const tagManager = integrations.find(
      (integration) =>
        integration.provider === "google_tag_manager" &&
        validPublicId(integration.provider, integration.publicIdentifier) &&
        allowed(integration.consentCategory),
    );
    if (tagManager) {
      const id = tagManager.publicIdentifier.trim();
      window.dataLayer = window.dataLayer ?? [];
      if (!document.getElementById("enkey-google-tag-manager")) {
        window.dataLayer.push({
          "gtm.start": Date.now(),
          event: "gtm.js",
        });
      } else {
        window.dataLayer.push({
          event: "page_view",
          page_path: `${location.pathname}${location.search}`,
        });
      }
      addScript(
        "enkey-google-tag-manager",
        `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`,
      );
    } else {
      removeScript("enkey-google-tag-manager");
    }
    syncResourceHints(
      "enkey-google-hint",
      Boolean(analyticsAllowed || tagManager),
      "https://www.googletagmanager.com",
    );

    const meta = integrations.find(
      (integration) =>
        integration.provider === "meta_pixel" &&
        validPublicId(integration.provider, integration.publicIdentifier) &&
        allowed(integration.consentCategory),
    );
    if (meta) {
      initializeMetaPixel(meta.publicIdentifier.trim(), `${location.pathname}${location.search}`);
      addScript(
        "enkey-meta-pixel",
        "https://connect.facebook.net/en_US/fbevents.js",
      );
    } else {
      removeScript("enkey-meta-pixel");
      for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim();
        if (name === "_fbp" || name === "_fbc") clearCookie(name);
      }
    }
    syncResourceHints(
      "enkey-meta-hint",
      Boolean(meta),
      "https://connect.facebook.net",
    );
  }, [consent, integrations, location.pathname, location.search]);

  return null;
}
